import { Trans, t } from "@helpers/translate";
import React, { useEffect, useMemo, useState } from "react";
import Link from "next/link";
import { useAuth } from "@helpers/hooks/useAuth";
import { useQuery } from "react-query";
import { graphql } from "@helpers/graphql";
import { User } from "@graphql/types";
import { Badge } from "../../../../components/ui/Badge";
import { Navbar } from "../../../../components/ui/Navbar";
import { Avatar } from "../../../../components/ui/Avatar";
import InputDestination from "../components/InputDestination";
import UserDropdown from "../components/UserDropdown";
import Preferences from "../components/Preferences";
import { getAbsoluteUrl } from "@helpers/getAbsoluteUrl";
import { ROUTES } from "@root/routes";

const NavbarUser = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const {
    auth,
    logout,
    refreshNotificationBadge,
    setRefreshNotificationBadge,
  } = useAuth();

  const { data, refetch } = useQuery(["UserNotification"], async () => {
    const res = await graphql.UserNotification();
    return (res.me as User).customerNotification;
  });

  useEffect(() => {
    if (refreshNotificationBadge) {
      refetch();
      setRefreshNotificationBadge(false);
    }
  }, [refreshNotificationBadge]);

  const notificationAvailable = useMemo(() => {
    if (data?.unReadMessagesCount || 0 > 0) {
      return true;
    }
    return false;
  }, [data]);

  if (!auth) {
    return null;
  }

  return (
    <Navbar fixed={true}>
      <Navbar.Search onClick={() => setIsSearchOpen(!isSearchOpen)} />
      <Navbar.Brand>
        <div className="mx-0 md:mx-4">
          <InputDestination isOpen={isSearchOpen} setIsOpen={setIsSearchOpen} />
        </div>
      </Navbar.Brand>

      <Navbar.Menu
        onClick={() => setIsOpen(!isOpen)}
        hasNotification={notificationAvailable}
      />

      <Navbar.Right>
        <Navbar.Nav>
          <Navbar.NavItem className="hidden lg:block px-6">
            <Preferences />
          </Navbar.NavItem>

          <Navbar.NavItem className="hidden lg:block px-0">
            <Link
              href="/auth/messages"
              title={t({
                id: "common.link.title",
                message: "GoWithGuide - Private Tours & Local Tour Guides",
              })}
            >
              <Badge count={data?.unReadMessagesCount}>
                <Trans id="navbar.messages">Messages</Trans>
              </Badge>
            </Link>
          </Navbar.NavItem>

          <Navbar.NavItem className="hidden lg:block">
            <Link
              href="/auth/bookings"
              title={t({
                id: "common.link.title",
                message: "GoWithGuide - Private Tours & Local Tour Guides",
              })}
            >
              <Trans id="navbar.bookings">Bookings</Trans>
            </Link>
          </Navbar.NavItem>
          <Navbar.NavItem className="hidden lg:block">
            <Link
              href="/contact"
              title={t({
                id: "common.link.title",
                message: "GoWithGuide - Private Tours & Local Tour Guides",
              })}
            >
              <Trans id="navbar.help">Help</Trans>
            </Link>
          </Navbar.NavItem>
          <Navbar.NavItem>
            <UserDropdown notification={data} />
          </Navbar.NavItem>
        </Navbar.Nav>
      </Navbar.Right>
      {isOpen && (
        <Navbar.MenuLayer isOpen={isOpen} onClick={() => setIsOpen(false)}>
          <div className="flex items-center mb-4">
            <div className="flex items-center font-semibold text-lg whitespace-nowrap overflow-hidden">
              <Avatar src={auth.picture?.url} size="xs" className="mr-2" />
              <span className="overflow-hidden text-ellipsis">
                {auth.displayName}
              </span>
            </div>
          </div>
          <Navbar.Divider />
          <Navbar.MenuItem href="/auth/account">
            <Trans id="navbar.account">Account</Trans>
          </Navbar.MenuItem>

          <Navbar.MenuItem href="/auth/messages">
            <Badge count={data?.unReadMessagesCount}>
              <Trans id="navbar.messages">Messages</Trans>
            </Badge>
          </Navbar.MenuItem>

          <Navbar.MenuItem href="/auth/bookings">
            <Trans id="navbar.bookings">Bookings</Trans>
          </Navbar.MenuItem>

          {auth.isBlogger && (
            <Navbar.MenuItem href="/auth/posts">
              <Trans id="navbar.blog">Blog</Trans>
            </Navbar.MenuItem>
          )}

          <Navbar.MenuItem href="/auth/reviews">
            <Trans id="navbar.reviews">Reviews</Trans>
          </Navbar.MenuItem>

          <Navbar.MenuItem href={getAbsoluteUrl(ROUTES.auth.loyalty())}>
            <Trans id="navbar.faq">Loyalty Program</Trans>
          </Navbar.MenuItem>

          <Navbar.MenuItem href="/contact">
            <Trans id="navbar.help">Help</Trans>
          </Navbar.MenuItem>
          <Navbar.Divider />
          <Navbar.MenuItem>
            <Preferences />
          </Navbar.MenuItem>
          <div className="text-lg font-medium my-4 block hover:underline cursor-pointer text-red-500">
            <a
              title={t({
                id: "common.link.title",
                message: "GoWithGuide - Private Tours & Local Tour Guides",
              })}
              href="#"
              onClick={() => logout("", true)}
            >
              <Trans id="navbar.logout">Logout</Trans>
            </a>
          </div>
        </Navbar.MenuLayer>
      )}
    </Navbar>
  );
};

export default NavbarUser;
